//-- Copyright (c) 2021 TMT Insights Group, LLC
import { BreakpointsOptions, createTheme, darken } from '@mui/material/styles';

export const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1366,
    xl: 1690
  },
  keys: ['xs', 'sm', 'md', 'lg', 'xl']
} satisfies BreakpointsOptions;

const theme = createTheme({ breakpoints });
const fontFamily = ['Open Sans', '-apple-system', 'BlinkMacSystemFont', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(',');
const letterSpacing = 'normal';

export const typography = {
  fontFamily,
  fontWeightLight: 400,
  fontWeightNormal: 500,
  fontWeightMedium: 600,
  fontWeightBold: 900,
  body: { fontFamily, letterSpacing, fontSize: '1rem' },
  body1: { fontFamily, letterSpacing, fontSize: '1rem', fontWeight: 500 },
  body2: { fontFamily, letterSpacing, fontSize: '1rem', fontWeight: 600 },
  h1: { fontFamily, letterSpacing, fontSize: '3rem' },
  h2: { fontFamily, letterSpacing, fontSize: '2.5rem', fontWeight: 400 },
  h3: { fontFamily, letterSpacing, fontSize: '2rem', fontWeight: 700 },
  h4: { fontFamily, letterSpacing, fontSize: '1.75rem', fontWeight: 700 },
  h5: { fontFamily, letterSpacing, fontSize: '1.66rem', fontWeight: 500 },
  h6: { fontFamily, letterSpacing, fontSize: '1.4rem' },
  subtitle1: { fontFamily, fontWeight: 700, color: `var(--primary-light)` },
  subtitle2: { fontFamily, letterSpacing, fontSize: '.98rem', fontWeight: 700, textTransform: 'none' },
  caption: { fontFamily, letterSpacing, fontWeight: 700, fontSize: '.86rem', opacity: 0.5 },
  button: { fontFamily, letterSpacing }
};

export const colors = {
  light: {
    primary: {
      main: '#4C11A2',
      dark: '#1C0B4F',
      light: '#ae71ff'
    },
    secondary: {
      main: '#3365F1',
      light: '#809be6',
      dark: '#001f77'
    },
    tertiary: {
      main: '#C400ED',
      light: '#e77afd',
      dark: '#66007b',
      contrastText: '#000000'
    },
    success: {
      main: '#66EB92',
      dark: '#4b9e5d',
      light: '#84AB93'
    },
    warning: {
      main: '#ffa726',
      light: '#ffb74d',
      dark: '#f57c00'
    },
    pending: {
      main: '#ea6f29'
    },
    error: {
      main: '#f44336',
      dark: '#d32f2f',
      light: '#e57373'
    },
    background: {
      default: '#F5F5F5',
      paper: '#ffffff'
    },
    info: {
      main: '#3887f4',
      light: '#3999c8',
      dark: '#00375f'
    },
    default: {
      main: '#949191',
      light: '#F5F5F5',
      dark: '#616161'
    },
    teal: {
      main: '#0dcbbe',
      light: '#52bbc1',
      dark: '#0a9c8d',
      contrastText: '#000000'
    },
    lavender: {
      main: '#c38fe3',
      light: '#cfa6e7',
      dark: '#7c4e9d',
      contrastText: '#000000'
    },
    divider: 'rgba(0, 0, 0, 0.16)',
    filters: {
      drawer: '#ffffff',
      cancel: '#838384',
      collapse: '#2ABDBB',
      clear: '#85878D',
      accordion: {
        background: '#F5F5F5',
        icon: '#85878D'
      },
      input: {
        background: '#ffffff',
        focused: '#ffffff',
        border: '#a4a4a4'
      }
    }
  },
  dark: {
    primary: {
      main: '#4C11A2',
      dark: '#1C0B4F',
      light: '#ae71ff'
    },
    secondary: {
      main: '#3365F1',
      light: '#809be6',
      dark: '#001f77'
    },
    tertiary: {
      main: '#C400ED',
      light: '#e77afd',
      dark: '#66007b',
      contrastText: '#ffffff'
    },
    success: {
      main: '#1fcd1c',
      dark: '#006e18',
      light: '#6ec782'
    },
    warning: {
      main: '#ffc72c',
      light: '#ffe34c',
      dark: '#c19200'
    },
    pending: {
      main: '#ea6f29'
    },
    error: {
      main: '#ed535d',
      dark: '#df2d39'
    },
    background: {
      default: '#212121',
      paper: '#1b1d27'
    },
    info: {
      main: '#3887f4',
      light: '#29bef4',
      dark: '#00375f'
    },
    default: {
      main: '#828d9f',
      light: '#778399',
      dark: '#393a3e'
    },
    teal: {
      main: '#4ccbd2',
      light: '#70dae0',
      dark: '#0a9c8d',
      contrastText: '#ffffff'
    },
    lavender: {
      main: '#cc90f1',
      light: '#C6A9D8',
      dark: '#7c4e9d',
      contrastText: '#ffffff'
    },
    filters: {
      drawer: '#131415',
      cancel: '#838384',
      collapse: '#2ABDBB',
      clear: '#85878D',
      accordion: {
        background: '#1A1B1F',
        icon: '#BBBFD1'
      },
      input: {
        background: '#25272C',
        focused: '#313339',
        border: '#313339'
      },
      text: {
        tertiary: '#85878D'
      }
    }
  }
};

export const fullWidthTheme = {
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          transition: 'max-width 0.25s ease 0.25s',
          maxWidth: '100%'
        }
      },
      defaultProps: {
        maxWidth: '100%'
      }
    }
  }
};

export const fullWidthThemeNoAnimate = {
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          transition: null,
          maxWidth: 2200
        }
      },
      defaultProps: {
        maxWidth: 2200
      }
    }
  }
};

export const globalBaseTheme = {
  breakpoints,
  typography,
  customOverrides: {
    Header: {
      background: 'linear-gradient(to right, #111111, #09322d)',
      '& h1 img': {
        height: 40,
        marginTop: -5,
        marginBottom: -5
      }
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          WebkitFontSmoothing: 'auto',
          fontSize: 12,
          lineHeight: 16,
          [theme.breakpoints.up('lg')]: {
            fontSize: 13,
            lineHeight: 17
          },
          [theme.breakpoints.up('xl')]: {
            fontSize: 14,
            lineHeight: 18
          }
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius,
          '&:before': {
            display: 'none'
          },
          '& .MuiAccordionDetails-root': {
            padding: theme.spacing(0, 1)
          },
          '& .MuiAccordionSummary-root': {
            flexDirection: 'row-reverse',
            padding: 0,
            minHeight: 'auto'
          },
          '& .MuiAccordionSummary-content': {
            margin: 0
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            '&.Mui-expanded': {
              transform: 'rotate(90deg)',
              margin: 0
            }
          }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiChip-root': {
            margin: 0,
            marginTop: 6
          },
          '& .MuiInputBase-sizeSmall': {
            fontSize: '.94rem'
          }
        },
        paper: {
          marginTop: 8
        },
        endAdornment: {
          // top: `calc(50% - 12px)`
        }
      }
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          MuiModal: {
            backgroundColor: 'rgb(0 0 0 / 77%)'
          }
        }
      }
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        ol: {
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          lineHeight: 1.5
        },
        li: {
          fontSize: '1.2rem',
          lineHeight: 1,
          '& a, & > h3, & > h2': {
            fontSize: '1.2rem'
          },
          '& .MuiButton-root': {
            height: 'auto',
            fontSize: '1.2rem',
            padding: '0 10px',
            display: 'inline-flex',
            alignItems: 'center',
            margin: 0
          }
        },
        separator: {
          opacity: 0.8,
          fontSize: '1.2rem',
          lineHeight: 1,
          fontWeight: 500,
          marginLeft: 10,
          marginRight: 10
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          textTransform: 'none'
        },
        sizeSmall: {
          fontWeight: typography.fontWeightBold,
          textTransform: 'none',
          fontSize: '.9rem',
          padding: '5px 10px'
        }
      }
    },
    MuiButtonGroup: {
      styleOverrides: {
        contained: {
          boxShadow: 'none'
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        colorTeal: {
          color: '#14B8A6', // teal-500
          '&:hover': {
            backgroundColor: 'rgba(20, 184, 166, 0.04)'
          },
          '&:disabled': {
            color: 'rgba(20, 184, 166, 0.3)'
          }
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 30,
          '& .MuiPaper-root': {
            boxShadow: 'none'
          }
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: 30,
          paddingBottom: 0
        },
        title: {
          fontSize: '1.5rem'
        },
        subheader: {
          fontSize: '1rem',
          opacity: 0.8,
          lineHeight: 2
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        // root: {
        //   fontSize: '.85rem'
        // },
        // label: {
        //   letterSpacing: 0.2,
        //   lineHeight: 1.2,
        //   color: 'white'
        // },
        // sizeSmall: {
        //   height: 21
        // },
        // sizeLarge: {
        //   height: 32,
        //   textTransform: 'uppercase',
        //   border: 0
        // },
        icon: {
          marginLeft: 8
        },
        labelSmall: {
          paddingLeft: 5,
          paddingRight: 5
        }
        // outlinedSecondary: {
        //   backgroundColor: '#FDAB433b',
        //   border: '1px solid #FDAB43'
        // },
        // outlinedInfo: {
        //   backgroundColor: '#4E43FD3b',
        //   border: '1px solid #4E43FD'
        // },
      }
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          transition: 'max-width 0.25s ease 0.25s',
          maxWidth: 2200
        }
      },
      defaultProps: {
        maxWidth: null
      }
    },
    MuiFab: {
      styleOverrides: {
        primary: {
          backgroundSize: `200% auto`,
          transition: `0.5s`,
          '&:hover': {
            backgroundPosition: `right center`,
            backgroundColor: 'var(--primary-light)'
          }
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&:before, &:after': {
            display: 'none'
          },
          '&.Mui-disabled': {
            opacity: 0.66
          }
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontWeight: 600
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontWeight: 'normal'
        },
        contained: {
          marginLeft: 7,
          marginRight: 7,
          opacity: 0.8
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        sizeSmall: {
          fontSize: '.95rem'
        },
        root: {
          '&.Mui-disabled': {
            opacity: 0.66
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: 'var(--primary-light)'
          },
          '&.Mui-disabled': {
            opacity: 0.66
          }
        },
        asterisk: {
          lineHeight: 1,
          fontSize: '1.2rem',
          verticalAlign: 'middle',
          display: 'inline-block',
          color: `var(--primary-main)`
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontWeight: typography.fontWeightBold,
          textDecoration: 'none'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontWeight: 500
        }
      }
    },
    MuiMenu: {
      defaultProps: {
        disableScrollLock: true
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 600
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--default-main)'
            }
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        },
        elevation20: {
          boxShadow: '0px 10px 13px -6px rgb(0 0 0 / 4%), 0px 20px 31px 3px rgb(0 0 0 / 3%), 0px 8px 38px 7px rgb(0 0 0 / 2%)'
        }
      },
      defaultProps: {
        elevation: 20
      }
    },
    MuiSelect: {
      iconOutlined: {
        right: 10,
        top: `calc(50% - 10px)`
      }
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& tr': {
            '& td': {
              // transition: 'ease background 200ms'
            },
            '&:hover td': {
              // transition: 'ease background 200ms'
            }
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          '& .MuiFormControl-root': {
            marginTop: 0
          },
          '& th': {
            textTransform: 'uppercase',
            fontSize: '.8rem',
            fontWeight: typography.fontWeightMedium,
            border: 0,
            paddingBottom: 8,
            opacity: 0.5
          }
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& tr th': {
            textTransform: 'uppercase',
            fontSize: '.8rem',
            fontWeight: typography.fontWeightMedium,
            border: 0,
            paddingBottom: 8,
            '& > span': {
              opacity: 0.5
            }
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiSvgIcon-root': {
            opacity: 0.8
          }
        }
      }
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          padding: 5
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '.9rem',
          lineHeight: 1.33,
          padding: '8px 10px',
          whiteSpace: 'pre-line'
        }
      }
    }
  }
};

export const lightBaseTheme = createTheme(
  {
    palette: {
      mode: 'light',
      ...colors.light
    },
    customOverrides: {
      TitleBar: {
        background: '#ffffff',
        boxShadow: '0px 4px 18px rgba(40, 41, 61, 0.07)'
      },
      Footer: {},
      TypeLabel: {},
      ExpandRow: {
        borderColor: colors.light.primary.light
      }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--primary-main': colors.light.primary.main,
            '--primary-light': colors.light.primary.light,
            '--primary-dark': colors.light.primary.dark,
            '--secondary-main': colors.light.secondary.main,
            '--secondary-light': colors.light.secondary.light,
            '--secondary-dark': colors.light.secondary.dark,
            '--tertiary-main': colors.light.tertiary.main,
            '--tertiary-light': colors.light.tertiary.light,
            '--tertiary-dark': colors.light.tertiary.dark,
            '--success-main': colors.light.success.main,
            '--success-light': colors.light.success.light,
            '--success-dark': colors.light.success.dark,
            '--teal-main': colors.light.teal.main,
            '--teal-light': colors.light.teal.light,
            '--info-main': colors.light.info.main,
            '--info-light': colors.light.info.light,
            '--info-dark': colors.light.info.dark,
            '--default-main': colors.light.default.main,
            '--default-light': colors.light.default.light,
            '--default-dark': colors.light.default.dark
          }
        }
      },
      MuiCard: {
        defaultProps: {
          elevation: 1
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: 'linear-gradient(156deg, #fff 20%, #eff2f5 100%)',
            borderColor: '#b4d5ff'
          },
          paperAnchorLeft: {
            background: 'linear-gradient(-156deg, #fff 20%, #eff2f5 100%)'
          }
        }
      },
      MuiFab: {
        styleOverrides: {
          primary: {
            backgroundImage: `linear-gradient(to right, var(--primary-light) 0%, var(--primary-main) 51%, var(--primary-light) 100%)`
          }
        }
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(0, 0, 0, 0.09)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.13)'
            },
            '&.Mui-disabled, &.Mui-disabled:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.09)'
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            border: `1px solid ${colors.light.divider}`,
            '&.MuiPaper-elevation': {
              boxShadow: '0px 4px 18px rgba(40, 41, 61, 0.1)'
            }
          }
        },
        defaultProps: {
          elevation: 8
        }
      }
    }
  },
  globalBaseTheme
);

export const darkBaseTheme = createTheme(
  {
    palette: {
      mode: 'dark',
      ...colors.dark
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--primary-main': colors.dark.primary.main,
            '--primary-light': colors.dark.primary.light,
            '--primary-dark': colors.dark.primary.dark,
            '--secondary-main': colors.dark.secondary.main,
            '--secondary-light': colors.dark.secondary.light,
            '--secondary-dark': colors.dark.secondary.dark,
            '--tertiary-main': colors.dark.tertiary.main,
            '--tertiary-light': colors.dark.tertiary.light,
            '--tertiary-dark': colors.dark.tertiary.dark,
            '--success-main': colors.dark.success.main,
            '--success-light': colors.dark.success.light,
            '--success-dark': colors.dark.success.dark,
            '--teal-main': colors.dark.teal.main,
            '--teal-light': colors.dark.teal.light,
            '--info-main': colors.dark.info.main,
            '--info-light': colors.dark.info.light,
            '--info-dark': colors.dark.info.dark,
            '--default-main': colors.dark.default.main,
            '--default-light': colors.dark.default.light,
            '--default-dark': colors.dark.default.dark
          }
        }
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: darken(colors.dark.background.paper, 0.3)
          }
        }
      },
      MuiButton: {
        variants: [
          {
            props: { variant: 'containedDefault' },
            style: {
              '&:hover': {
                backgroundColor: 'rgb(85 93 108 / 15%)'
              }
            }
          }
        ],
        styleOverrides: {
          root: {
            fontWeight: typography.fontWeightMedium
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            color: '#fff',
            '&.MuiChip-filledInfo': {
              backgroundColor: darken(colors.dark.info.main, 0.25)
            },
            '&.MuiChip-filledTeal': {
              backgroundColor: darken(colors.dark.teal.main, 0.5)
            }
          }
        }
      },
      MuiFab: {
        styleOverrides: {
          primary: {
            backgroundImage: `linear-gradient(to right, var(--primary-dark) 0%, var(--primary-main) 51%, var(--primary-light) 100%)`
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: 'linear-gradient(156deg, #1a1d25 20%, #080809 100%)',
            borderColor: '#232b32'
          },
          paperAnchorLeft: {
            background: 'linear-gradient(-156deg, #1a1d25 20%, #080809 100%)'
          }
        }
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(171, 171, 220, 0.15)',
            '&:hover, &.Mui-focused': {
              backgroundColor: 'rgba(171, 171, 220, 0.18)'
            },
            '&.Mui-disabled, &.Mui-disabled:hover': {
              backgroundColor: 'rgba(171, 171, 220, 0.15)'
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: darken(colors.dark.background.default, 0.5)
          }
        }
      }
    }
  },
  globalBaseTheme
);
